import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { prettyDate } from "@/lib/equi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Users,
  Sparkles,
  AlarmClockOff,
  Loader2,
  Trash2,
  ShieldCheck,
  Shield,
  Eye,
  ExternalLink,
  ChevronLeft,
  Stethoscope,
  X,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, onClick, active, testid }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      data-testid={testid}
      className={`text-left w-full bg-card border rounded-xl p-5 ${
        onClick ? "transition-shadow hover:shadow-lg cursor-pointer" : ""
      } ${active ? "border-primary ring-1 ring-primary" : "border-border"}`}
    >
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide font-semibold">
        <Icon className="h-4 w-4 text-primary" /> {label}
      </div>
      <p className="text-3xl font-extrabold mt-2 font-mono">{value}</p>
    </Comp>
  );
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso || "—";
  }
}

export default function AdminConsole() {
  const { user, setActingAs } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // user id acting on
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all"); // all | farm | vet

  // Drill-in state
  const [viewUser, setViewUser] = useState(null);
  const [farmMares, setFarmMares] = useState([]);
  const [farmLoading, setFarmLoading] = useState(false);
  const [mareHistory, setMareHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
      setStats(s.data);
      setUsers(u.data);
    } catch {
      toast.error("Could not load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [load, user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard/daily" replace />;

  const trialAction = async (u, action, days) => {
    setBusy(u.id);
    try {
      await api.post(`/admin/users/${u.id}/trial`, { action, days });
      toast.success(action === "reset" ? "Trial reset to 60 days" : `Extended by ${days} days`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const toggleRole = async (u) => {
    const role = u.role === "admin" ? "user" : "admin";
    setBusy(u.id);
    try {
      await api.post(`/admin/users/${u.id}/role`, { role });
      toast.success(`Now ${role}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success("Account deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const openFarm = async (u) => {
    setViewUser(u);
    setMareHistory(null);
    setFarmLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/mares`);
      setFarmMares(data.mares);
    } catch {
      toast.error("Could not load farm");
    } finally {
      setFarmLoading(false);
    }
  };

  const openLiveFarm = (u) => {
    setActingAs({ tenant_id: u.tenant_id, label: u.farm_name || u.name || u.email, email: u.email });
    navigate("/dashboard/daily");
  };

  const openMareHistory = async (mare) => {
    if (!viewUser) return;
    setHistLoading(true);
    try {
      const year = new Date().getFullYear();
      const { data } = await api.get(`/admin/users/${viewUser.id}/mares/${mare.id}/history/${year}`);
      setMareHistory(data);
    } catch {
      toast.error("Could not load history");
    } finally {
      setHistLoading(false);
    }
  };

  const trialBadge = (u) => {
    if (u.role === "admin") return <Badge variant="secondary">No trial (admin)</Badge>;
    if (u.trial_expired) return <Badge variant="destructive">Expired</Badge>;
    return <Badge variant="secondary">{u.trial_days_left}d left</Badge>;
  };

  const filteredUsers = users.filter((u) => {
    if (typeFilter === "all") return true;
    if (typeFilter === "expired") return u.role !== "admin" && u.trial_expired;
    return (u.account_type || "farm") === typeFilter;
  });

  const filterLabel = { farm: "Farms", vet: "Veterinarians", expired: "Expired subscriptions" }[typeFilter];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Admin Console</h1>
        <p className="text-muted-foreground text-sm">Manage farms, trials and access across the platform.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Users} label="Farms" value={stats?.farm_users ?? 0} onClick={() => setTypeFilter((f) => (f === "farm" ? "all" : "farm"))} active={typeFilter === "farm"} testid="admin-stat-farms" />
            <StatCard icon={Stethoscope} label="Vets" value={stats?.vet_users ?? 0} onClick={() => setTypeFilter((f) => (f === "vet" ? "all" : "vet"))} active={typeFilter === "vet"} testid="admin-stat-vets" />
            <StatCard icon={Sparkles} label="Active trials" value={stats?.active_trials ?? 0} />
            <StatCard icon={AlarmClockOff} label="Expired" value={stats?.expired_trials ?? 0} onClick={() => setTypeFilter((f) => (f === "expired" ? "all" : "expired"))} active={typeFilter === "expired"} testid="admin-stat-expired" />
          </div>

          {typeFilter !== "all" && (
            <div className="flex items-center gap-2 mb-4" data-testid="admin-filter-bar">
              <span className="text-sm text-muted-foreground">
                Showing <strong className="text-foreground">{filterLabel}</strong> ({filteredUsers.length})
              </span>
              <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => setTypeFilter("all")} data-testid="admin-filter-clear">
                <X className="h-3.5 w-3.5" /> Clear
              </Button>
            </div>
          )}

          <div className="border border-border rounded-xl bg-card overflow-x-auto equi-scroll">
            <table className="w-full text-sm min-w-[1080px]">
              <thead>
                <tr className="bg-primary text-primary-foreground text-xs uppercase tracking-wide">
                  <th className="px-4 py-3 text-left font-semibold">Farm / User</th>
                  <th className="px-4 py-3 text-left font-semibold">Type</th>
                  <th className="px-4 py-3 text-left font-semibold">Role</th>
                  <th className="px-4 py-3 text-left font-semibold">Signed up</th>
                  <th className="px-4 py-3 text-left font-semibold">Trial</th>
                  <th className="px-4 py-3 text-center font-semibold">Mares</th>
                  <th className="px-4 py-3 text-center font-semibold">Records</th>
                  <th className="px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const isSelf = u.id === user?.id;
                  const isLegacyOwner = u.tenant_id === "default";
                  const isVetAcct = u.account_type === "vet";
                  return (
                    <tr key={u.id} className="border-t border-border align-top" data-testid={`admin-user-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{u.farm_name || u.name || "—"}</div>
                        <div className="text-muted-foreground text-xs">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {isVetAcct ? (
                          <Badge variant="secondary" className="gap-1"><Stethoscope className="h-3 w-3" /> Vet</Badge>
                        ) : (
                          <Badge variant="outline">Farm</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {u.role === "admin" ? (
                          <Badge className="gap-1"><ShieldCheck className="h-3 w-3" /> Admin</Badge>
                        ) : (
                          <Badge variant="outline">User</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3">{trialBadge(u)}</td>
                      <td className="px-4 py-3 text-center font-mono">{u.mare_count}</td>
                      <td className="px-4 py-3 text-center font-mono">{u.record_count}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => openFarm(u)} data-testid={`admin-view-${u.id}`} title="Quick preview (mares & history)">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" className="h-8 px-2 gap-1 text-xs" onClick={() => openLiveFarm(u)} data-testid={`admin-open-${u.id}`} title="Open this farm's dashboard read-only">
                            <ExternalLink className="h-4 w-4" /> Open
                          </Button>
                          {u.role !== "admin" && (
                            <>
                              <Button variant="outline" size="sm" className="h-8 px-2 text-xs" disabled={busy === u.id} onClick={() => trialAction(u, "extend", 30)} data-testid={`admin-trial30-${u.id}`}>
                                +30d
                              </Button>
                              <Button variant="outline" size="sm" className="h-8 px-2 text-xs" disabled={busy === u.id} onClick={() => trialAction(u, "extend", 60)}>
                                +60d
                              </Button>
                              <Button variant="outline" size="sm" className="h-8 px-2 text-xs" disabled={busy === u.id} onClick={() => trialAction(u, "reset", 60)} data-testid={`admin-reset-${u.id}`}>
                                Reset
                              </Button>
                            </>
                          )}
                          {!isSelf && (
                            <Button variant="outline" size="sm" className="h-8 px-2" disabled={busy === u.id} onClick={() => toggleRole(u)} data-testid={`admin-role-${u.id}`} title={u.role === "admin" ? "Make user" : "Make admin"}>
                              {u.role === "admin" ? <Shield className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                            </Button>
                          )}
                          {!isSelf && !isLegacyOwner && (
                            <Button variant="outline" size="sm" className="h-8 px-2 text-destructive border-destructive/40 hover:bg-destructive/10" onClick={() => setDeleteTarget(u)} data-testid={`admin-delete-${u.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Read-only farm drill-in */}
      <Sheet open={!!viewUser} onOpenChange={(o) => { if (!o) { setViewUser(null); setMareHistory(null); } }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{viewUser?.farm_name || viewUser?.name || viewUser?.email}</SheetTitle>
            <SheetDescription>{viewUser?.email} · read-only</SheetDescription>
          </SheetHeader>

          {mareHistory ? (
            <div className="mt-4">
              <Button variant="ghost" size="sm" className="gap-1 mb-3" onClick={() => setMareHistory(null)}>
                <ChevronLeft className="h-4 w-4" /> Back to mares
              </Button>
              <h3 className="font-bold text-lg">{mareHistory.mare_name} · {mareHistory.year}</h3>
              <p className="text-sm text-muted-foreground mb-3">{mareHistory.total} records · {mareHistory.filled} with entries</p>
              {mareHistory.records.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No records this year.</p>
              ) : (
                <div className="border border-border rounded-lg overflow-hidden">
                  {mareHistory.records.map((r) => (
                    <div key={r.id} className="border-b border-border last:border-0 px-3 py-2 text-sm">
                      <div className="font-semibold">{prettyDate(r.date)}</div>
                      <div className="text-muted-foreground text-xs">
                        L:{r.left_ovary || "—"} · R:{r.right_ovary || "—"} · Fluid:{r.fluid || "—"} · Edema:{r.edema || "—"} · Cervix:{r.cervix || "—"}
                      </div>
                      {r.notes && <div className="italic text-muted-foreground text-xs mt-0.5">{r.notes}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4">
              {farmLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
              ) : farmMares.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">This farm has no mares yet.</p>
              ) : (
                <div className="space-y-2">
                  {farmMares.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => openMareHistory(m)}
                      disabled={histLoading}
                      className="w-full text-left flex items-center justify-between px-3 py-2.5 rounded-lg border border-border hover:bg-accent/40 transition-colors"
                      data-testid={`admin-farm-mare-${m.id}`}
                    >
                      <div>
                        <div className="font-semibold text-sm">{m.name}</div>
                        {m.stallion && <div className="text-xs text-muted-foreground">Stallion: {m.stallion}</div>}
                      </div>
                      <Badge variant="outline" className="font-mono">{m.record_count} rec</Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.farm_name || deleteTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the account and ALL of its mares, records, teasing entries and settings. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-delete-confirm">
              Delete account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
